import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth, googleProvider } from '@/config/firebase';
import { apiClient } from '@/lib/api-client';
import { clearLocalDatabase } from '@/lib/db';
import { clearE2EUser, readE2EUser } from '@/lib/e2e';
import type { CurrentUser, UserRole } from '@/types/user';

interface AuthState {
  /** The authenticated user profile, or null if not signed in. */
  user: CurrentUser | null;
  /** Firebase user object for token management. */
  firebaseUser: FirebaseUser | null;
  /** True while checking initial auth state. */
  loading: boolean;
  /** Error message from the last auth operation. */
  error: string | null;
  /** Sign in with Google popup. */
  signInWithGoogle: () => Promise<void>;
  /** Sign in with email and password. */
  signInWithEmail: (email: string, password: string) => Promise<void>;
  /** Sign out and clear local data. */
  signOut: () => Promise<void>;
  /** Check if the current user has a specific role. */
  hasRole: (role: UserRole | UserRole[]) => boolean;
  /** Check if the current user is an admin. */
  isAdmin: boolean;
  /** Check if the current user is a platform super-admin. */
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * AuthProvider wraps the app and manages Firebase authentication state.
 *
 * On sign-in, it fetches the user profile from /api/auth/me to get
 * server-side role, tenant, and team information.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const e2eUser = useMemo(() => readE2EUser(), []);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch the user profile from the backend after Firebase auth succeeds.
   */
  const fetchUserProfile = useCallback(async (): Promise<CurrentUser | null> => {
    try {
      const profile = await apiClient.get<CurrentUser>('/auth/me');
      return profile;
    } catch (err) {
      console.error('[Auth] Failed to fetch user profile:', err);
      return null;
    }
  }, []);

  /**
   * Listen for Firebase auth state changes.
   */
  useEffect(() => {
    if (e2eUser) {
      setUser(e2eUser);
      setFirebaseUser(null);
      setLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        // User is signed in — fetch their profile from the backend
        const profile = await fetchUserProfile();
        if (profile) {
          setUser(profile);
        } else {
          // Backend doesn't know this user (not provisioned)
          setUser({
            uid: fbUser.uid,
            email: fbUser.email ?? '',
            displayName: fbUser.displayName ?? '',
            photoURL: fbUser.photoURL,
            role: 'viewer',
            tenantId: '',
            teamIds: [],
            qualifications: '',
            registrationNumber: '',
          });
        }
      } else {
        setUser(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [e2eUser, fetchUserProfile]);

  /**
   * Sign in with Google popup.
   */
  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
      // onAuthStateChanged will handle the rest
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      setError(message);
      console.error('[Auth] Google sign-in failed:', err);
    }
  }, []);

  /**
   * Sign in with email and password.
   */
  const signInWithEmail = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged will handle the rest
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      setError(message);
      console.error('[Auth] Email sign-in failed:', err);
    }
  }, []);

  /**
   * Sign out, clear local database, and reset state.
   */
  const signOut = useCallback(async () => {
    try {
      await clearLocalDatabase();
      if (e2eUser) {
        clearE2EUser();
        setUser(null);
        setFirebaseUser(null);
        return;
      }
      await firebaseSignOut(auth);
      setUser(null);
      setFirebaseUser(null);
    } catch (err) {
      console.error('[Auth] Sign-out failed:', err);
    }
  }, [e2eUser]);

  /**
   * Check if the current user has one of the specified roles.
   */
  const hasRole = useCallback(
    (role: UserRole | UserRole[]): boolean => {
      if (!user) return false;
      const roles = Array.isArray(role) ? role : [role];
      return roles.includes(user.role);
    },
    [user],
  );

  const isAdmin = useMemo(() => user?.role === 'admin', [user]);
  const isSuperAdmin = useMemo(() => user?.isSuperAdmin === true, [user]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      firebaseUser,
      loading,
      error,
      signInWithGoogle,
      signInWithEmail,
      signOut,
      hasRole,
      isAdmin,
      isSuperAdmin,
    }),
    [user, firebaseUser, loading, error, signInWithGoogle, signInWithEmail, signOut, hasRole, isAdmin, isSuperAdmin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth state. Must be used within an AuthProvider.
 */
export function useAuthContext(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
